import { allPageList } from './pagesBook';
import {
    IChildrenActionPage,
    IComponentName,
    IPage,
    IPageAction,
    IPageAliases,
    IPageList,
    IParentPages,
} from '../../interfaces';

export class ChildrenOfParentsPages {
    private readonly _map: IParentPages = {
        ActivityLogList: {},
        BalanceLogList: {},
        BillingCompaniesList: { VIEW: 'BillingCompany', EDIT: 'BillingCompanyEdit', NEW: 'BillingCompanyNew' },
        BillingOrdersList: { VIEW: 'BillingShowOrder' },
        CallDataRecordsList: {},
        CallDataRecordsListBeta: {},
        CardList: { VIEW: 'Card', EDIT: 'CardEdit' },
        CardSetsList: { VIEW: 'CardSet', EDIT: 'CardSetEdit', NEW: 'CardSetNew' },
        CompaniesList: { VIEW: 'Company', EDIT: 'CompanyEdit', NEW: 'CompanyNew' },
        CountryList: { VIEW: 'Country', EDIT: 'CountryEdit', NEW: 'CountryNew' },
        CurrenciesList: { VIEW: 'Currency', EDIT: 'CurrencyEdit' },
        DataImport: {},
        DiameterSessionsList: { VIEW: 'DiameterSessionsList', EDIT: 'DiameterSessionsList' },
        ESPlusNotificationQRcodes: {},
        ESPlusNotifications: {},
        ESPlusNotificationsLogs: {},
        ExternalAPIList: { EDIT: 'ExternalAPIEdit' },
        FeatureFlags: { EDIT: 'FeatureFlagsEdit' },
        GatewayConfig: {},
        ImpersonateUser: {},
        InvoicesList: { VIEW: 'Invoice' },
        MonthlyOrdersList: { VIEW: 'MonthlyOrder' },
        MonthlyUsageList: {},
        MyCompany: { VIEW: 'MyCompany', EDIT: 'MyCompanyEdit' },
        OperatorInteractionsResponsesList: { VIEW: 'OperatorInteractionsResponse' },
        OperatorsList: { VIEW: 'Operator', EDIT: 'OperatorEdit', NEW: 'OperatorNew' },
        OrdersList: { VIEW: 'OrderView', EDIT: 'OrderView' },
        PackagesBuy: { VIEW: 'PackageInstance', EDIT: 'PackageInstanceEdit' },
        PackagesSell: { VIEW: 'PackageRoot', EDIT: 'PackageEdit', NEW: 'PackageNew' },
        PackagesUnitsLogsList: { VIEW: 'PackageInstanceBuyPreview' },
        PaperTrailLogsList: {},
        Profile: { VIEW: 'Profile', EDIT: 'ProfileEdit' },
        PurchaseRatesList: { VIEW: 'PurchaseRateList', EDIT: 'PurchaseRateListEdit', NEW: 'PurchaseRateListNew' },
        ReportsPageList: {},
        SalesRateLists: { VIEW: 'SalesRateListV1', EDIT: 'SalesRateListEditV1', NEW: 'SalesRateListNewV1' },
        SalesRateListsV2: { VIEW: 'SalesRateListV2', EDIT: 'SalesRateListEditV2', NEW: 'SalesRateListNewV2' },
        ScpFileUpload: {},
        SubscribersList: { VIEW: 'Subscriber', EDIT: 'SubscriberEdit', NEW: 'SubscriberNew' },
        TariffPackageInstancesList: {},
        TariffToBuyList: { VIEW: 'TariffToBuyItem' },
        TariffPackagesToBuyList: { VIEW: 'TariffPackagesToBuyItem' },
        UsersList: { VIEW: 'User', EDIT: 'UserEdit', NEW: 'UserNew' },
        ZonesList: { VIEW: 'Zone' },
    };

    private readonly _pageAliases: IPageAliases = {
        MyCompany: 'Company',
        MyCompanyEdit: 'CompanyEdit',
        Profile: 'User',
        ProfileEdit: 'UserEdit',
    };
    private getAliasPage(page: IComponentName): IComponentName {
        return this._pageAliases[page] || page;
    }

    private checkParent(parentPage: IPageList): void {
        if (!this._map[parentPage] && process.env.NODE_ENV === 'development') {
            console.error(`Parent page: "${parentPage}" not found`);
        }
    }
    // ------------  ------------
    public getParent(alias: IComponentName): IPageList | undefined {
        const page = this.getAliasPage(alias);
        let foundParent = undefined;
        Object.entries(this._map).forEach(([parent, children]) => {
            if (parent === page) {
                foundParent = page;
            }
            if (children) {
                Object.entries(children).forEach(([, child]) => {
                    if (child === page) {
                        foundParent = parent;
                    }
                });
            }
        });
        return (foundParent || page) as IPageList | undefined;
    }

    public getChildren(parentPage: IPageList): IChildrenActionPage | undefined {
        this.checkParent(parentPage);
        return this._map[parentPage];
    }

    public getChildrenOfTypeByParents(parentPage: IPageList, type: IPageAction): IComponentName | undefined {
        this.checkParent(parentPage);
        return this._map[parentPage]?.[type];
    }

    public getChildrenOfTypeByChildren(page: IComponentName, type: IPageAction): IComponentName | undefined {
        let childrenByType: IComponentName | undefined = undefined;
        for (const [parentPage, children] of Object.entries(this._map)) {
            let isFoundParent = false;
            for (const [, child] of Object.entries(children as IChildrenActionPage)) {
                if (child === page) {
                    isFoundParent = true;
                }
            }
            if (isFoundParent) {
                childrenByType = this._map[parentPage as IPageList]?.[type];
            }
        }
        return childrenByType;
    }
}

export const getComponentInfo = (page: IComponentName): IPage => {
    return allPageList.find((c: IPage) => c.componentName === page) as IPage;
};

export const getReplaceComponentName = (p: IComponentName): IComponentName => {
    return p === 'MyProfileList' ? 'UsersList' : p === 'MyCompanyList' ? 'CompaniesList' : p;
};

export const getParenName = (childrenPage: IComponentName): IPageList | undefined => {
    const childrenOfParentsPages = new ChildrenOfParentsPages();
    return childrenOfParentsPages.getParent(childrenPage);
};

export const getParenIdOfChildren = (childrenPage: IComponentName): string | undefined => {
    const parentName = getParenName(childrenPage);
    return parentName && getComponentInfo(parentName).id;
};

export const getChildren = (parentPage: IPageList): IChildrenActionPage | undefined => {
    const childrenOfParentsPages = new ChildrenOfParentsPages();
    return childrenOfParentsPages.getChildren(parentPage);
};

export const getChildrenOfTypeByChildren = (
    childrenPage: IComponentName,
    type: IPageAction,
): IComponentName | undefined => {
    const childrenOfParentsPages = new ChildrenOfParentsPages();
    return childrenOfParentsPages.getChildrenOfTypeByChildren(childrenPage, type);
};
